/**
 * Copiere în clipboard pentru valori sensibile (parole, coduri TOTP, coduri
 * de recuperare), cu golire automată după un interval.
 *
 * Clipboard-ul e vizibil oricărei aplicații și, pe unele sisteme, sincronizat
 * între dispozitive — un secret nu are ce căuta acolo pe termen nelimitat.
 */
const CLEAR_AFTER_MS = 30_000;

let clearTimer: number | null = null;

async function writeClipboard(text: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    // Fallback pentru contexte fără Clipboard API (HTTP simplu, browsere vechi).
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    try {
      document.execCommand('copy');
    } finally {
      document.body.removeChild(ta);
    }
  }
}

/** `true` dacă putem citi clipboard-ul fără să declanșăm un prompt. */
async function canReadSilently(): Promise<boolean> {
  try {
    const status = await navigator.permissions.query({
      name: 'clipboard-read' as PermissionName,
    });
    return status.state === 'granted';
  } catch {
    return false;
  }
}

/**
 * Copiază `secret` și programează golirea clipboard-ului. Dacă între timp
 * userul a copiat altceva (și browserul ne lasă să verificăm), nu atingem
 * clipboard-ul; dacă nu putem verifica, îl golim oricum.
 */
export async function copySecret(secret: string): Promise<void> {
  await writeClipboard(secret);
  if (clearTimer !== null) window.clearTimeout(clearTimer);
  clearTimer = window.setTimeout(() => {
    clearTimer = null;
    void (async () => {
      try {
        if (await canReadSilently()) {
          if ((await navigator.clipboard.readText()) !== secret) return;
        }
        await navigator.clipboard.writeText('');
      } catch {
        // fereastra nu mai are focus sau API indisponibil — nimic de făcut
      }
    })();
  }, CLEAR_AFTER_MS);
}
